import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Image, Form, Alert } from 'react-bootstrap';
import { FaTrash, FaArrowLeft, FaShoppingCart } from 'react-icons/fa';
import { useCart } from '../context/CartContext';

const CartPage = () => {
  const { cart, removeItem, increaseItemQuantity, decreaseItemQuantity, clearCart } = useCart();

  const handleRemoveItem = (itemId) => {
    removeItem(itemId);
  };

  const handleIncreaseQuantity = (itemId) => {
    increaseItemQuantity(itemId);
  };

  const handleDecreaseQuantity = (itemId) => {
    decreaseItemQuantity(itemId);
  };

  const handleClearCart = () => {
    if (window.confirm('Are you sure you want to clear your cart?')) {
      clearCart();
    }
  };

  // Calculate totals
  const subtotal = cart.items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  // const shipping = subtotal > 0 ? 10 : 0; // Example shipping cost
  const shipping = 0;
  const tax = subtotal * 0.08; // Example tax rate (8%)
  const total = subtotal + shipping + tax;

  return (
    <Container className="py-5">
      <h1 className="mb-4">Your Shopping Cart</h1>

      {cart.items.length === 0 ? (
        <Card className="text-center p-5 mb-4">
          <Card.Body>
            <FaShoppingCart size={50} className="text-muted mb-3" />
            <h2>Your cart is empty</h2>
            <p className="mb-4">Looks like you haven't added anything to your cart yet.</p>
            <Link to="/products" className="btn btn-primary">
              Continue Shopping
            </Link>
          </Card.Body>
        </Card>
      ) : (
        <Row>
          {/* Cart Items */}
          <Col lg={8}>
            <Card className="mb-4">
              <Card.Header className="bg-white">
                <Row className="fw-bold">
                  <Col md={6}>Product</Col>
                  <Col md={2} className="text-center">Price</Col>
                  <Col md={2} className="text-center">Quantity</Col>
                  <Col md={2} className="text-center">Total</Col>
                </Row>
              </Card.Header>
              <Card.Body>
                {cart.items.map((item) => (
                  <Row key={item.id} className="py-3 border-bottom align-items-center">
                    <Col md={6} className="d-flex align-items-center">
                      <div className="me-3" style={{ width: '80px', height: '80px' }}>
                        <Image
                          src={item.image_url || "https://via.placeholder.com/80"}
                          alt={item.name}
                          className="img-fluid"
                          style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                        />
                      </div>
                      <div>
                        <h5 className="mb-1">
                          <Link to={`/products/${item.id}`} className="text-decoration-none">
                            {item.name}
                          </Link>
                        </h5>
                        <Button
                          variant="link"
                          className="text-danger p-0"
                          onClick={() => handleRemoveItem(item.id)}
                        >
                          <FaTrash size={14} className="me-1" /> Remove
                        </Button>
                      </div>
                    </Col>
                    <Col md={2} className="text-center">
                     ₹{(typeof item.price === 'number' ? item.price : parseFloat(item.price || 0)).toFixed(2)}
                    </Col>
                    <Col md={2} className="text-center">
                      <div className="d-flex justify-content-center">
                        <div className="input-group" style={{ width: '100px' }}>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleDecreaseQuantity(item.id)}
                            disabled={item.quantity <= 1}
                          >
                            -
                          </Button>
                          <input
                            type="text"
                            className="form-control form-control-sm text-center"
                            value={item.quantity}
                            readOnly
                          />
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleIncreaseQuantity(item.id)}
                          >
                            +
                          </Button>
                        </div>
                      </div>
                    </Col>
                    <Col md={2} className="text-center fw-bold">
                     ₹{((typeof item.price === 'number' ? item.price : parseFloat(item.price || 0)) * item.quantity).toFixed(2)}
                    </Col>
                  </Row>
                ))}
              </Card.Body>
              <Card.Footer className="bg-white d-flex justify-content-between">
                <Link to="/products" className="btn btn-outline-primary">
                  <FaArrowLeft className="me-2" /> Continue Shopping
                </Link>
                <Button variant="outline-danger" onClick={handleClearCart}>
                  Clear Cart
                </Button>
              </Card.Footer>
            </Card>
          </Col>

          {/* Order Summary */}
          <Col lg={4}>
            <Card>
              <Card.Header className="bg-white">
                <h4 className="mb-0">Order Summary</h4>
              </Card.Header>
              <Card.Body>
                <div className="d-flex justify-content-between mb-2">
                  <span>Subtotal</span>
                  <span>₹{(typeof subtotal === 'number' ? subtotal : parseFloat(subtotal || 0)).toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Shipping</span>
                  <span>₹{(typeof shipping === 'number' ? shipping : parseFloat(shipping || 0)).toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between mb-3">
                  <span>Tax (8%)</span>
                  <span>₹{(typeof tax === 'number' ? tax : parseFloat(tax || 0)).toFixed(2)}</span>
                </div>
                <hr />
                <div className="d-flex justify-content-between mb-3 fw-bold fs-5">
                  <span>Total</span>
                  <span>₹{(typeof total === 'number' ? total : parseFloat(total || 0)).toFixed(2)}</span>
                </div>

                <Form className="mb-3">
                  <Form.Group className="mb-3">
                    <Form.Label>Discount Code</Form.Label>
                    <div className="d-flex">
                      <Form.Control type="text" placeholder="Enter code" className="me-2" />
                      <Button variant="outline-secondary">Apply</Button>
                    </div>
                  </Form.Group>
                </Form>

                <Button 
                  variant="primary" 
                  size="lg" 
                  className="w-100" 
                  as={Link} 
                  to="/checkout"
                  disabled={cart.items.length === 0}
                >
                  Proceed to Checkout
                </Button>
                
                {cart.items.length === 0 && (
                  <div className="text-danger text-center mt-2">
                    <small>Add items to your cart to proceed</small>
                  </div>
                )}
              </Card.Body>
            </Card>

            <Alert variant="info" className="mt-3">
              <small>
                <strong>Note:</strong> Free shipping on orders over ₹50000.
                Items ship within 2-3 business days.
              </small>
            </Alert>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default CartPage; 